<template>
  <span>
    <el-table ref="payoutTable" stripe :data="filteredReferrals" style="width: 100%"
      @selection-change="onSelectionChange"
      :default-sort="{ prop: forCashPayout ? 'returnStatus' : 'dateAdded', order: 'ascending' }">
      <el-table-column type="selection" width="55" v-if="forCashPayout" :selectable="isSelectable" />
      <el-table-column label="Status" property="status" width="175" sortable>
        <template slot-scope="{ row }"><raf-status-tag :raf-status="row.status"
            :is-paid="row.transactionId > 0"></raf-status-tag></template>
      </el-table-column>
      <el-table-column property="officeId" label="Office" width="100" v-if="!forCashPayout">
        <template slot-scope="{ row }">{{ getUserIdForOffice(row.officeId) }}</template>
      </el-table-column>
      <el-table-column property="newClientName" label="New Client Name" />
      <el-table-column property="newClientSSN" label="New Client SSN" />
      <el-table-column property="statusReason" label="Status Reason" min-width="120" />
      <el-table-column property="amount" label="Amount" v-if="forCashPayout">
        <template slot-scope="{ row }">${{ row.amount }}</template>
      </el-table-column>
      <el-table-column property="dateAdded" label="Date" sortable>
        <template slot-scope="{ row }">{{ formatDate(row.dateAdded) }}</template>
      </el-table-column>
      <template v-if="isAdmin && !forCashPayout">
        <el-table-column fixed="right" label="Actions" width="250">
          <template slot-scope="{ row }">
            <el-dropdown size="small" @command="handleModifyClick" v-if="!isPaid(row)">
              <el-button type="danger" size="small">
                Modify Referral <i class="el-icon-arrow-down el-icon--right" />
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item :command="{ method: changeOffice, row: row }">Change Office</el-dropdown-item>
                <el-dropdown-item :command="{ method: override, row: row }">Override</el-dropdown-item>
                <el-dropdown-item :command="{ method: deleteReferral, row: row }">Delete</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </template>
    </el-table>
    <el-dialog title="Change Referral Office" :visible.sync="changeOfficeVm.visible" :show-close="false"
      :close-on-click-modal="false" :close-on-press-escape="false" width="400px">
      <change-office-dialog :officeId="changeOfficeVm.officeId" @close="changeOfficeVm.visible = false"
        @save="changeOfficeForSelectedReferral"></change-office-dialog>
    </el-dialog>
    <el-dialog title="Override Referral" :visible.sync="overrideVm.visible" :show-close="false"
      :close-on-click-modal="false" :close-on-press-escape="false" width="400px">
      <override-referral-dialog :status="overrideVm.status" :status-reason="overrideVm.statusReason"
        :amount="overrideVm.amount" @close="overrideVm.visible = false"
        @save="overrideSelectedReferral"></override-referral-dialog>
    </el-dialog>
  </span>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import filter from 'lodash/filter';
import ChangeOfficeDialog from '@/components/dialog-content/ChangeOffice.vue';
import OverrideReferralDialog from '@/components/dialog-content/OverrideReferral.vue';
import RafStatusTag from '@/components/tags/RAFStatus.vue';
import { RAF_STATE } from '@/enum/RAFStatuses';

const DAILY_CLIENT_RAF_PAYOUT_LIMIT = 3;

export default {
  name: 'RAFTable',
  components: {
    ChangeOfficeDialog,
    OverrideReferralDialog,
    RafStatusTag,
  },
  props: {
    forCashPayout: {
      type: Boolean,
      default: false,
    },
    referrals: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      changeOfficeVm: {
        visible: false,
        officeId: null,
        referral: null,
      },
      overrideVm: {
        visible: false,
        amount: null,
        Rstatus: null,
        statusReason: null,
        referral: null,
      },
    };
  },
  methods: {
    ...mapActions('Lookup', ['getOffices']),
    isSelectable(row) {
      return row.status === RAF_STATE.APPROVED;
    },
    formatDate(date) {
      return moment(date).format('L LT');
    },
    onSelectionChange(selection) {
      const limitedSelection = selection.slice(0, DAILY_CLIENT_RAF_PAYOUT_LIMIT);
      const excessSelection = selection.slice(DAILY_CLIENT_RAF_PAYOUT_LIMIT);
      if (excessSelection.length) {
        this.$message(`Only ${DAILY_CLIENT_RAF_PAYOUT_LIMIT} referrals can be paid to a client in cash per day.`);

        excessSelection.forEach((row) => {
          this.$refs.payoutTable.toggleRowSelection(row, false);
        });
      }

      this.$emit('selectionChange', limitedSelection);
    },
    autoSelectAcceptedRows() {
      if (this.filteredReferrals) {
        this.filteredReferrals
          .filter(row => row.status === RAF_STATE.APPROVED)
          .slice(0, DAILY_CLIENT_RAF_PAYOUT_LIMIT)
          .forEach((row) => {
            this.$nextTick(() => {
              this.$refs.payoutTable.toggleRowSelection(row, true);
            });
          });
      }
    },
    getUserIdForOffice(id) {
      return (this.officeMap[id] ? this.officeMap[id].userId : '');
    },
    changeOffice(referral) {
      this.changeOfficeVm.referral = referral;
      this.changeOfficeVm.officeId = referral.officeId;
      this.changeOfficeVm.visible = true;
    },
    override(referral) {
      this.overrideVm.referral = referral;
      this.overrideVm.status = referral.status;
      this.overrideVm.statusReason = referral.statusReason;
      this.overrideVm.amount = referral.amount;
      this.overrideVm.visible = true;
    },
    changeOfficeForSelectedReferral(officeId) {
      const referral = this.changeOfficeVm.referral;
      this.changeOfficeVm.officeId = officeId;
      this.$emit('changeReferralOffice', {
        referral,
        officeId,
      });
    },
    overrideSelectedReferral(referral) {
      const fullReferral = { ...this.overrideVm.referral, ...referral };
      this.$emit('overrideReferral', fullReferral);
    },
    isPaid(referral) {
      return !!referral.transactionId;
    },
    deleteReferral(referral) {
      this.$confirm('Are you sure want to delete this referral?').then(() => {
        this.$emit('deleteReferral', referral);
      }).catch(() => { });
    },
    handleModifyClick({ method, row }) {
      method(row);
    },
  },
  watch: {
    filteredReferrals() {
      this.autoSelectAcceptedRows();
    },
  },
  computed: {
    ...mapGetters('User', [
      'isAdmin',
    ]),
    ...mapGetters('Lookup', [
      'officeMap',
    ]),
    filteredReferrals() {
      if (this.forCashPayout) {
        return filter(this.referrals, referral => !referral.transactionId);
      }
      return this.referrals;
    },
  },
  mounted() {
    this.getOffices();
    this.autoSelectAcceptedRows();
  },
};

</script>

<style lang="scss" scoped>
.client-header {
  h2 {
    margin: 0;
    padding: 0 5px;
    font-size: 1.8em;

    span {
      font-weight: normal;
      color: #999;
      font-size: 0.75em;
      text-transform: uppercase;
    }
  }
}
</style>
